import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { createSelector } from 'reselect';

import { getExporterMeasurableFields } from '../../../lib/exporterHelper';
import GridLayout from '../GridLayout';
import FieldModes from '../../../constants/FieldModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';

const StyledWrapper = styled.div`
  display: flex;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
`;

/**
 * ExportFormPage handles specific requirements around formatting
 * for PDF Export. For instance, it measures the fields to dispaly
 * all internal contents, it adjusts the pages to support proper
 * page-break points, etc.
 */
const ExportFormPage = (props) => {

  const {

    id, //Page._id

    //PDF Config
    config,

    //Page Properties
    cols,
    width,
    layout,
    fields,

    /**
     * What does margin do? This was a legacy implementation that we had for supporting the external whitespace from
     * the border that was visible on a page. This is something that we implemented for honeywell POC but no longer use.
     */
    rowHeight,
    margin,
    padding,
    borderWidth,

    licenseValidation,
    measure,
    onMeasurementsCompleted

  } = props;


  /**
   * The one list of fields this page waits for.
   *
   * Computed once and shared by the two paths below, so "does this page complete immediately",
   * "is this measurement one we asked for" and "have all of them arrived" can never disagree.
   * layout is an input because a float page admits a different set from a grid page (see
   * getExporterMeasurableFields).
   */
  const measurableFields = useMemo(
    () => (fields && fields.length > 0 ? getExporterMeasurableFields(fields, licenseValidation, layout) : []),
    [fields, licenseValidation, layout]
  );

  /**
   * Handle Meaurement For Page With No MeasurableFields
   * ==================================================
   *
   * IMPORTANT NOTE: DO NOT REMOVE. This is used to trigger measurement completed if measurement is required and there are no valid fields
   * or no measurable fields. The PDF Exporter waits for each page to be measured before rendering the PDF and call onMeasurementsCompleted
   * even if there are no measurable fields.
   */
  useEffect(() => {
    if (measurableFields.length < 1 && onMeasurementsCompleted) onMeasurementsCompleted(id, {});
  }, [id, measurableFields, onMeasurementsCompleted]);

  /**
   * Handle Meaurement For Page With Measurable Fields
   * ================================================
   *
   * This is used to listen for the measurement events from each individual measurable field. Once all
   * fields have been measured, we call onMeasurementsCompleted with the measurements for the page.
   */
  const measurementsRef = useRef({});
  const handleFieldMeasurementCompleted = useCallback((fieldId, fieldIdentifier, fieldPositionId, rect) => {

    const measureableFieldFound = measurableFields.find((field) => field._id === fieldPositionId);
    if (!measureableFieldFound) return;

    let nextRect;

    if (Number.isFinite(rect.autoHeightOverflowPx)) {

      /**
       * An auto-height field on a float page. It kept its drawn box and reported how much of its
       * value did not fit, the same two numbers the builder records on blur. They are already in
       * the page's own pixels (the page was drawn at page.width with its own rowHeight), so they
       * pass through untouched; the grid conversion below is not applied and no height is stored,
       * so nothing downstream can mistake a converted box for the answer.
       */
      nextRect = {
        autoHeightOverflowPx: rect.autoHeightOverflowPx,
        autoHeightBoxPx: rect.autoHeightBoxPx,
        measuredOn: rect.measuredOn,
      };

    } else if (Number.isFinite(rect.textOverflowPx)) {

      /**
       * A wrapping single-line text field on a grid page: plain, auto-shrink, auto-height or
       * both. It stayed at its drawn size while it was measured and reported only the pixels
       * its text needs beyond that box, 0 for a value that fits. So the drawn rows are kept
       * and whole rows are added for the overflow alone: no natural height and no +4, which is
       * what turned every 20px field into a 40px one. Number.isFinite rather than a truthy
       * check, so a reported 0 lands here and keeps the box.
       *
       * rowHeight falls back to 8, GridLayout's default for a page saved without one. A
       * rowHeight that is not a positive number adds nothing rather than dividing by it.
       */
      const effectiveRowHeight = rowHeight === undefined ? 8 : rowHeight;

      const extraRows = (Number.isFinite(effectiveRowHeight) && effectiveRowHeight > 0)
        ? Math.ceil(rect.textOverflowPx / effectiveRowHeight)
        : 0;

      nextRect = { ...rect, height: measureableFieldFound.height + extraRows };

    } else {

      /**
       * Grid pages. Handle measured height by converting to proper react-grid-layout row height.
       *
       * Why +4? The field has a transparent border of 1px all around
       * and 1px padding all around that is not accounted for in the
       * internal height calculation.
       */
      const measuredHeight = (rect.height + 4) / rowHeight;

      nextRect = { ...rect, width: Math.ceil(rect.width + 4), height: Math.ceil(measuredHeight) };

    }

    measurementsRef.current[fieldPositionId] = nextRect;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (Object.keys(measurementsRef.current).length === measurableFields.length && onMeasurementsCompleted) {
      onMeasurementsCompleted(id, { ...measurementsRef.current });
    }

  }, [id, measurableFields, rowHeight, onMeasurementsCompleted]);

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   *
   * A float page is not squeezed like that. The PDF renderer draws it at exactly page.width and
   * fits the whole thing to the paper with one CSS scale afterwards, so the measuring pass has
   * to lay the fields out at page.width too, or the text would wrap at a different column and
   * the measured overflow would describe a box the reader never sees. cols equals page.width on
   * these pages, so one grid column is one pixel.
   */
  const adjustedContainerWidth = layout === PageLayoutModes.float
    ? width
    : getAdjustedDimension(config.page.width, (padding + config.page.padding), margin, borderWidth);

  return (
    <StyledWrapper {...{ width: adjustedContainerWidth }}>
      <GridLayout
        {...props}
        mode={FieldModes.pdf}
        fields={fields}
        width={adjustedContainerWidth}
        cols={cols}
        enableLazyLoadFields={false}
        licenseValidation={licenseValidation}
        enableFieldMeasurementRendering
        onFieldMeasurementCompleted={handleFieldMeasurementCompleted}
      />
    </StyledWrapper>
  );

};

export const getMeasurableFieldSelector = createSelector(
  (state) => state.fields,
  (state) => state.licenseValidation,
  (state) => state.layout,
  (fields, licenseValidation, layout) => {
    return getExporterMeasurableFields(fields, licenseValidation, layout);
  }
);

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export default ExportFormPage;
